import { useEffect, useState } from 'preact/hooks';
import { api, type Speed } from '../api.js';
import { useStore } from '../store.js';
import { zec } from '../format.js';

/**
 * How fast things are, measured live and said plainly. Trades settle in our
 * ledger in milliseconds; money moving on Zcash takes Zcash blocks. The page
 * keeps the two apart so no number promises more than it means.
 */
export function SpeedPage() {
  const s = useStore();
  const [sp, setSp] = useState<Speed | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.speed().then((x) => alive && setSp(x)).catch(() => {});
    void load();
    const t = setInterval(load, 10_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  const fast = s.info?.beta.fastDepositMaxZat;
  const conf = s.info?.beta.minConfirmations ?? 3;
  const ms = (v: number | null | undefined) => (v === null || v === undefined ? '…' : `${v} ms`);
  return (
    <div style={{ maxWidth: 760 }}>
      <h1 class="page-title">Speed</h1>
      <p class="muted">Measured on this server from the last {sp?.samples ?? 0} trades and launches. Updated every 10 seconds.</p>
      <div class="token-stats" style={{ margin: '20px 0 28px' }}>
        <div class="stat">
          <div class="k">Trade confirmed, typical</div>
          <div class="v">{ms(sp?.p50Ms)}</div>
        </div>
        <div class="stat">
          <div class="k">95% of trades within</div>
          <div class="v">{ms(sp?.p95Ms)}</div>
        </div>
        <div class="stat">
          <div class="k">99% within</div>
          <div class="v">{ms(sp?.p99Ms)}</div>
        </div>
      </div>
      <table class="list kv">
        <tbody>
          <tr>
            <td>Buy, sell, launch a coin</td>
            <td>Ordered, executed, signed and saved by the server in the time above. Your signed receipt comes back at once.</td>
          </tr>
          <tr>
            <td>Deposit</td>
            <td>
              {fast && fast !== '0' && conf > 1
                ? `Up to ${zec(fast)} ZEC: ready after 1 Zcash block. Larger: after ${conf} blocks.`
                : `Ready after ${conf} Zcash block${conf === 1 ? '' : 's'}.`}{' '}
              A
              Zcash block comes about every 75 seconds.
            </td>
          </tr>
          <tr>
            <td>Withdrawal</td>
            <td>Sent to the Zcash network within seconds of your request; it arrives when the next block includes it.</td>
          </tr>
          <tr>
            <td>Proof on Zcash</td>
            <td>Every trade is written into Zcash at least every 10 minutes, sooner when the market is busy.</td>
          </tr>
        </tbody>
      </table>
      <p class="muted" style={{ fontSize: 13, marginTop: 16 }}>
        The trade time is how long our server takes, not a Zcash block: trades settle in Dazzle's public ledger first and are written into
        Zcash after. Your network adds its own delay on top.
      </p>
    </div>
  );
}
